import { FC, MouseEvent, useEffect, useMemo } from 'react'
import {
  HamburgerSectionHeader,
  SaturatedItem,
  selectSaturatedTree,
  stencil,
  TreeNode,
  TreeNodeOrNodeSlug,
} from 'react-common'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useSidebarLayers } from '@/features/layout/hooks/useSidebarLayers'
import { useShouldShowSidebar } from './useShouldShowSidebar'
import { useCommonNavigationItems } from '../StencilPreset/hooks/useCommonNavigationItems'
import { useLayoutVisibility } from '../StencilPreset/hooks/useLayoutVisibility'
import { useStencilNavigation } from '../useStencilNavigation'

const { HamburgerPlugin, HamburgerItemComponent } = stencil

type HamburgerItemProps = {
  item: TreeNodeOrNodeSlug
  isActive?: boolean
  onClick: (evt: MouseEvent<HTMLElement>) => void
}

const HamburgerItem: FC<HamburgerItemProps> = ({ item, isActive, onClick }) => {
  if (typeof item === 'string') return null

  if (item.children?.length) {
    return (
      <HamburgerSectionHeader key={item.name} title={item.name}>
        {item.children.map((child) => (
          <li key={child.name}>
            <HamburgerItemComponent item={child as SaturatedItem} onClick={onClick} />
          </li>
        ))}
      </HamburgerSectionHeader>
    )
  }

  return (
    <li key={item.name}>
      <HamburgerItemComponent
        item={{
          url: item.url,
          name: item.name,
          icon_id: item.icon_id,
          slug: item.slug,
          isSection: false,
          parentSlug: null,
          target: item.target,
        }}
        isActive={isActive}
        onClick={onClick}
      />
    </li>
  )
}

export const HamburgerContainer: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { activeSlug, setActiveSlug } = useStencilNavigation()
  const { setLayers } = useSidebarLayers()

  const { hamburger: showHamburger } = useLayoutVisibility()

  const shouldShowSidebar = useShouldShowSidebar()
  const commonItems = useCommonNavigationItems()

  const hamburgerTree = useSelector(selectSaturatedTree) as Map<string, TreeNodeOrNodeSlug>
  const hasPortalAccess = hamburgerTree?.size > 0

  const hamburgerItems = useMemo(
    () => [
      ...(hasPortalAccess
        ? [
            {
              url: '/portal',
              name: t('goToPortal'),
              icon_id: 'RepeatBoldStroke',
              isSection: false,
              slug: 'portal',
              parentSlug: null,
            },
          ]
        : []),
      ...commonItems,
    ],
    [commonItems, hasPortalAccess, t],
  )

  useEffect(() => {
    if (shouldShowSidebar) {
      setLayers(commonItems)
    }
  }, [commonItems, shouldShowSidebar, setLayers])

  if (!showHamburger) return null

  const handleClick = (evt: MouseEvent<HTMLElement>, item: TreeNode) => {
    if (typeof item === 'string') return

    if (item.slug) {
      setActiveSlug(item.slug)
    }

    if (item.url?.startsWith('/learn')) {
      evt.preventDefault()
      navigate(item.url.split('/learn')[1])
    }
  }

  return (
    <HamburgerPlugin>
      {hamburgerItems.map((item) => (
        <HamburgerItem
          key={typeof item === 'string' ? item : item.name}
          item={item as TreeNode}
          isActive={typeof item !== 'string' && activeSlug === item.slug}
          onClick={(evt) => handleClick(evt, item as TreeNode)}
        />
      ))}
    </HamburgerPlugin>
  )
}
